import { useEffect, useMemo } from 'react';
import { useFetcher } from 'react-router';
import type { ContentSearchSuggestionsResponse } from '@/routes/resource.search.content-suggestions';
import { RECENT_SEARCH_MIN_LENGTH } from '@/components/eataly-header/search/search-constants';

const CONTENT_SUGGESTIONS_FETCHER_KEY = 'search-content-suggestions';

export function useContentSearchSuggestions(query: string, enabled = true, debounceMs = 0) {
    const fetcher = useFetcher<ContentSearchSuggestionsResponse>({ key: CONTENT_SUGGESTIONS_FETCHER_KEY });
    const trimmedQuery = query.trim();
    const shouldFetch = enabled && trimmedQuery.length >= RECENT_SEARCH_MIN_LENGTH;

    useEffect(() => {
        if (!shouldFetch) {
            return;
        }

        const params = new URLSearchParams({ q: trimmedQuery });
        const timer = setTimeout(() => {
            void fetcher.load(`/resource/search/content-suggestions?${params.toString()}`);
        }, debounceMs);

        return () => {
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when query, enabled, or debounce changes
    }, [trimmedQuery, shouldFetch, debounceMs]);

    return useMemo(
        () => ({
            hits: shouldFetch ? (fetcher.data?.hits ?? []) : [],
            isLoading: shouldFetch && fetcher.state === 'loading',
        }),
        [fetcher.data?.hits, fetcher.state, shouldFetch]
    );
}
